import { NavigateFunction } from 'react-router-dom';
import { fetchData, FetchDataError, FetchDataResponse } from './fetch-data';
import { URLParamsBuilder } from './url-params-builder';
import { FORBIDDEN_PATH, LOGIN_PATH, SERVICE_UNAVAILABLE_PATH } from 'user/routes';
import { LoginData } from 'auth/models';

export class FetcherService {
  accessToken: string;
  authenticate: () => Promise<LoginData>;
  navigate: NavigateFunction;
  clearTokens: () => void;
  _initialized = false;

  setup(
    accessToken: string,
    authenticate: () => Promise<LoginData>,
    navigate: NavigateFunction,
    clearTokens: () => void,
  ) {
    this.accessToken = accessToken;
    this.authenticate = authenticate;
    this.navigate = navigate;
    this.clearTokens = clearTokens;
    this._initialized = true;
  }

  async post<T>(
    url: string,
    body?: any,
    isFileRequest?: boolean,
    isPublic?: boolean,
  ): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({
      method: 'POST',
      url,
      body,
      isFileRequest,
      isPublic,
    });
  }

  async get<T>(
    url: string,
    queryParams?: any,
    isFileRequest?: boolean,
    isPublic?: boolean,
  ): Promise<FetchDataResponse<T>> {
    const _url = new URLParamsBuilder(url).addQueryParams(queryParams).build();
    return this.buildFetchRequest({ method: 'GET', url: _url, isFileRequest, isPublic });
  }

  async put<T>(url: string, body?: any): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({
      method: 'PUT',
      url,
      body,
    });
  }
  async patch<T>(url: string, body?: any): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({
      method: 'PATCH',
      url,
      body,
    });
  }

  async del<T>(url: string): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({ method: 'DELETE', url });
  }

  private async buildFetchRequest<T>(params: {
    method: string;
    url: string;
    headers?: HeadersInit;
    body?: any;
    isFileRequest?: boolean;
    isPublic?: boolean;
    defaultFileName?: string;
  }): Promise<FetchDataResponse<T>> {
    const {
      method,
      url,
      body,
      headers,
      isFileRequest = false,
      isPublic = false,
      defaultFileName,
    } = params;

    const defaultHeaders = {};

    if (!isPublic) {
      defaultHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    }

    if (!isFileRequest) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    try {
      return await fetchData<T>(
        method,
        url,
        { ...defaultHeaders, ...headers },
        body,
        isFileRequest,
        defaultFileName,
      );
    } catch (error) {
      switch (error.statusCode) {
        case 401:
          if ((error as FetchDataError).apiError?.message === 'AuthenticationExpired') {
            const tokens = await this.authenticate();
            if (tokens) {
              // Seteamos el nuevo access token
              this.setup(
                tokens.accessToken,
                this.authenticate,
                this.navigate,
                this.clearTokens,
              );
              // hacemos el refetch
              return this.buildFetchRequest({
                method,
                url,
                headers,
                body,
                isFileRequest,
              });
            } else {
              this.clearTokens();
              this.navigate(LOGIN_PATH);
            }
          } else if (!isPublic) {
            this.clearTokens();
            this.navigate(LOGIN_PATH);
          }
          break;
        case 403:
          this.navigate(FORBIDDEN_PATH);
          break;
        case 503:
          this.navigate(SERVICE_UNAVAILABLE_PATH);
          break;
      }
      throw error;
    }
  }
}
